import React, { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { usePurchaseInvoices } from '../../hooks/api/usePurchaseInvoices'

const PurchaseInvoiceListPage: React.FC = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [vendorName, setVendorName] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  const { data, isLoading, isError, refetch } = usePurchaseInvoices({
    page: page + 1,
    limit: rowsPerPage,
    vendor_name: vendorName || undefined,
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
  })

  const invoices = data?.data ?? []
  const total = data?.total ?? 0

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Purchase Invoices
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/purchase-invoices/new')}
          >
            Add Purchase Invoice
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Vendor Name"
              value={vendorName}
              onChange={(e) => { setVendorName(e.target.value); setPage(0) }}
              placeholder="Search by vendor..."
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(0) }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(0) }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setVendorName('')
                setFromDate('')
                setToDate('')
                setPage(0)
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load purchase invoices.
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Grand Total</TableCell>
                <TableCell>Payment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No purchase invoices found.
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((inv) => (
                  <TableRow
                    key={inv._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/purchase-invoices/${inv._id}`)}
                  >
                    <TableCell>{inv.vendor.name}</TableCell>
                    <TableCell>
                      {format(new Date(inv.invoice_date), 'dd MMM yyyy')}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {inv.summary.net_value_before_tax.toLocaleString('en-PK', {
                          style: 'currency',
                          currency: 'PKR',
                          maximumFractionDigits: 0,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={inv.payment_status === 'paid' ? 'Paid' : inv.payment_status === 'partial' ? 'Partial' : 'Unpaid'}
                        variant={inv.payment_status === 'paid' ? 'success' : inv.payment_status === 'partial' ? 'warn' : 'danger'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0) }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </Paper>
    </Box>
  )
}

export default PurchaseInvoiceListPage
